import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usePurchase } from '../context/PurchaseContext';

/**
 * Post-payment landing. Access is ONLY granted by the Stripe webhook on the server
 * — reaching this page does NOT unlock the course. We just confirm the payment was
 * received, poll the user's account to see when the webhook has flipped has_access,
 * and guide them to sign in / create an account if they haven't yet.
 */
export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser } = useAuth();
  const { hasAccess, claimStripe } = usePurchase();

  const [checking, setChecking] = useState(isAuthenticated);
  const [claimTried, setClaimTried] = useState(false);

  // When logged in, refresh user and attempt a manual Stripe claim after a short delay
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;
    const tick = async () => {
      if (cancelled) return;
      await refreshUser();
      if (!claimTried) {
        try { await claimStripe(); } catch { /* fine */ }
        setClaimTried(true);
      }
      attempts += 1;
      if (hasAccess || attempts >= maxAttempts) {
        setChecking(false);
        return;
      }
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <div className="max-w-xl mx-auto fade-in">
      <div className="bg-white rounded-3xl border-2 border-success/30 shadow-sm p-8 md:p-10 text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full mx-auto mb-4 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text mb-3">Payment received</h1>
        <p className="text-text-muted text-lg mb-6">
          Thank you! Stripe is confirming your payment. Your full course access will be unlocked on your account automatically.
        </p>

        {isAuthenticated ? (
          hasAccess ? (
            <div className="space-y-4">
              <div className="bg-success/10 border border-success/30 rounded-xl p-4 text-success font-semibold">
                Access unlocked — welcome aboard.
              </div>
              <Button onClick={() => navigate('/')} className="rounded-xl py-5 px-8 text-lg font-bold">Start learning</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {checking ? (
                <div className="flex items-center justify-center gap-2 text-text-muted">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Waiting for Stripe to confirm the payment…</span>
                </div>
              ) : (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-text-muted text-sm">
                  Stripe can take a few minutes. If your course isn't unlocked within 10 minutes, go to the <Link to="/purchase" className="text-primary font-semibold hover:underline">purchase page</Link> and click "Claim access".
                </div>
              )}
            </div>
          )
        ) : (
          <div className="space-y-4 text-left">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-text mb-1">One quick step to activate your course</p>
                  <p className="text-text-muted text-sm">Create a free account using the <strong>same email</strong> you used at checkout. We'll match your payment and unlock the course automatically.</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/signup" className="flex-1"><Button className="w-full rounded-xl py-5 text-lg font-bold">Create account</Button></Link>
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-xl py-5 text-lg font-bold">Sign in</Button></Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
